/**
 * A list of excerpts, managed in place (#277). Two uses:
 * - `source`: every excerpt in a document (the "Excerpts (n)" panel), each
 *   with its destinations; "Show" jumps to the text.
 * - `destination`: the Related list of a project, tag, View or person, and
 *   the Excerpts tab of a series - excerpts sent there, and for projects and
 *   series those that reached them through their items ("via Task: Fix
 *   login"), which a toggle hides. A series also lists the excerpts made in
 *   its meetings, with All / Made in these meetings / Sent here chips (#290).
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Scissors, X, ListTree, ExternalLink, Locate, Trash2 } from 'lucide-react';
import { ExcerptKindIcon } from './ExcerptKindIcon';
import { useExcerptStore } from '../../contexts/ExcerptSyncContext';
import { useToast } from '../../contexts/ToastContext';
import { ENTITY_COLORS, ENTITY_ICONS } from '../../lib/entityMeta';
import { listByDestination, listBySource, type DestType, type ExcerptView, type SourceType } from '../../lib/excerpts';
import { HISTORY_CHANGED_EVENT } from '../../lib/history';
import { DestinationPicker } from './DestinationPicker';
import { changeDestinations, itemUrl, nextMeetingToast, removeExcerpt, removeFrom, sourceUrl } from './excerptActions';

/** The same colours the excerpt's tint and icon use in the text. */
const KIND_CLASS = { excerpt: 'text-excerpt', task: 'text-excerpt-task', note: 'text-excerpt-note' } as const;
const TYPE_LABEL: Record<string, string> = {
  meeting_instance: 'Meeting', task: 'Task', note: 'Note', scratchpad: 'Scratchpad', scope: 'Project',
  meeting_series: 'Series', tag: 'Tag', reference_doc: 'View', person: 'Person',
};

type Props =
  | { mode: 'source'; type: SourceType; id: string; onJump?: (excerptId: string) => void; onChanged?: () => void }
  | { mode: 'destination'; type: DestType; id: string; rollup?: boolean; onChanged?: () => void;
      /** Leave these out entirely - e.g. a series lists task and note excerpts in its own Tasks and Notes tabs (#290). */
      hide?: (e: ExcerptView) => boolean };

export function ExcerptList(props: Props) {
  const { mode, type, id } = props;
  const rollupAllowed = props.mode === 'destination' && !!props.rollup && (type === 'scope' || type === 'meeting_series');
  const store = useExcerptStore();
  const toast = useToast();
  const navigate = useNavigate();
  const [loaded, setItems] = useState<ExcerptView[] | null>(null);
  const [showVia, setShowVia] = useState(true);
  const [seriesFilter, setSeriesFilter] = useState<'all' | 'made' | 'sent'>('all');
  const [editing, setEditing] = useState<ExcerptView | null>(null);

  const load = useCallback(async () => {
    try {
      setItems(mode === 'source' ? await listBySource(type as SourceType, id) : await listByDestination(type, id, rollupAllowed));
    } catch {
      setItems([]);
    }
  }, [mode, type, id, rollupAllowed]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    const reload = () => { void load(); };
    window.addEventListener(HISTORY_CHANGED_EVENT, reload);
    return () => window.removeEventListener(HISTORY_CHANGED_EVENT, reload);
  }, [load]);

  /** Runs fn and says `label`, unless `announce` reports the result itself (#360 next meetings). */
  const act = async <T,>(label: string, fn: () => Promise<T>, announce?: (result: T) => boolean) => {
    try {
      const result = await fn();
      if (!announce?.(result)) toast.success(label);
      await load();
      props.onChanged?.();
    } catch {
      toast.error('That did not work - please try again');
    }
  };

  if (loaded === null) return <div className="text-xs text-text-muted py-2">Loading excerpts...</div>;
  const hide = props.mode === 'destination' ? props.hide : undefined;
  const items = hide ? loaded.filter((e) => !hide(e)) : loaded;
  const isSeries = rollupAllowed && type === 'meeting_series';
  /** Sent to this item itself (so "Remove from here" applies), not only reached through a member. */
  const sentDirect = (e: ExcerptView) => e.destinations.some((d) => d.type === type && d.id === id);
  const sentHere = (e: ExcerptView) => sentDirect(e) || !!e.via;
  const visible = isSeries
    ? items.filter((e) => seriesFilter === 'all' || (seriesFilter === 'made' ? e.made_here : sentHere(e)))
    : showVia ? items : items.filter((e) => !e.via);
  const hasVia = items.some((e) => e.via);
  const chips: Array<[typeof seriesFilter, string, number]> = [
    ['all', 'All', items.length],
    ['made', 'Made in these meetings', items.filter((e) => e.made_here).length],
    ['sent', 'Sent here', items.filter(sentHere).length],
  ];

  return (
    <div className="space-y-2" data-testid={`excerpt-list-${mode}`}>
      {isSeries && items.length > 0 && (
        <div role="group" aria-label="Filter excerpts" className="flex flex-wrap gap-1.5">
          {chips.map(([key, label, n]) => (
            <button
              key={key}
              type="button"
              aria-pressed={seriesFilter === key}
              onClick={() => setSeriesFilter(key)}
              className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${seriesFilter === key
                ? 'border-border-hover bg-bg-input text-text-primary' : 'border-border text-text-secondary hover:text-text-primary'}`}
            >
              {label} <span className="text-text-muted">{n}</span>
            </button>
          ))}
        </div>
      )}
      {!isSeries && rollupAllowed && hasVia && (
        <label className="flex items-center gap-1.5 text-xs text-text-muted select-none cursor-pointer">
          <input type="checkbox" className="accent-accent" checked={showVia} onChange={(e) => setShowVia(e.target.checked)} />
          Include excerpts sent to its items
        </label>
      )}
      {visible.length === 0 && (
        <div className="text-xs text-text-muted py-2">
          {mode === 'source' ? 'No excerpts in this document yet.'
            : items.length > 0 ? 'No excerpts match this filter.'
            : isSeries ? 'No excerpts yet. Excerpts made in these meetings, or sent to the series, show up here.'
            : 'Nothing has been sent here yet. Select text in a meeting or note and choose Excerpt.'}
        </div>
      )}
      {visible.map((e) => {
        return (
          <div key={e.id} className="group rounded-md border border-border bg-bg-card px-3 py-2">
            <div className="flex items-start gap-2">
              <ExcerptKindIcon kind={e.kind} linkedId={e.linked_entity_id} size={14} className={`mt-0.5 shrink-0 ${KIND_CLASS[e.kind] ?? 'text-excerpt'}`} />
              <div className="min-w-0 flex-1">
                <div className="text-sm text-text-primary line-clamp-3 whitespace-pre-wrap">{e.plain_text || '(empty)'}</div>
                <div className="mt-1 text-[11px] text-text-muted">
                  {mode === 'destination' && (
                    <>from <button type="button" className="underline decoration-dotted hover:text-text-primary" onClick={() => navigate(sourceUrl(e))}>{e.source.title}</button></>
                  )}
                  {e.via && (
                    <> {' - via '}{TYPE_LABEL[e.via.type] ?? e.via.type}:{' '}
                      <button type="button" className="underline decoration-dotted hover:text-text-primary" onClick={() => navigate(itemUrl(e.via!.type, e.via!.id))}>{e.via.title}</button>
                    </>
                  )}
                </div>
                {mode === 'source' && (
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {e.destinations.map((d) => {
                      const DIcon = ENTITY_ICONS[d.type] ?? ListTree;
                      return (
                        <span key={`${d.type}:${d.id}`} className="inline-flex items-center gap-1 rounded bg-bg-input px-1.5 py-0.5 text-[11px] text-text-secondary">
                          <DIcon size={10} style={{ color: ENTITY_COLORS[d.type] }} />
                          <button type="button" className="max-w-[10rem] truncate hover:text-text-primary" onClick={() => navigate(itemUrl(d.type, d.id))}>{d.title}</button>
                          <button type="button" aria-label={`Remove from ${d.title}`} className="hover:text-danger" onClick={() => void act(`Removed from ${d.title}`, () => removeFrom(store, e, d))}>
                            <X size={10} />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-0.5 opacity-70 group-hover:opacity-100">
                {mode === 'source' && props.mode === 'source' && props.onJump && (
                  <button type="button" title="Show in the text" aria-label="Show in the text" className="p-1 rounded hover:bg-bg-hover" onClick={() => props.onJump!(e.id)}><Locate size={13} /></button>
                )}
                {mode === 'destination' && (
                  <button type="button" title="Open in context" aria-label="Open in context" className="p-1 rounded hover:bg-bg-hover" onClick={() => navigate(sourceUrl(e))}><ExternalLink size={13} /></button>
                )}
                <button type="button" title="Change destinations" aria-label="Change destinations" className="p-1 rounded hover:bg-bg-hover" onClick={() => setEditing(e)}><ListTree size={13} /></button>
                {mode === 'destination' && sentDirect(e) ? (
                  <button type="button" title="Remove from here" aria-label="Remove from here" className="p-1 rounded hover:bg-bg-hover hover:text-danger" onClick={() => void act('Removed from here', () => removeFrom(store, e, { type, id }))}><Trash2 size={13} /></button>
                ) : mode === 'source' ? (
                  <button type="button" title="Remove excerpt" aria-label="Remove excerpt" className="p-1 rounded hover:bg-bg-hover hover:text-danger" onClick={() => void act('Excerpt removed', () => removeExcerpt(store, e, false))}><Trash2 size={13} /></button>
                ) : null}
              </div>
            </div>
          </div>
        );
      })}
      {editing && (
        <DestinationPicker
          title="Change destinations"
          confirmLabel="Save"
          initial={editing.destinations.map((d) => ({ type: d.type as DestType, id: d.id, title: d.title }))}
          exclude={{ type: editing.source.type as DestType, id: editing.source.id }}
          onClose={() => setEditing(null)}
          onConfirm={(picked) => { const v = editing; setEditing(null); void act('Destinations updated', () => changeDestinations(store, v, picked), (reached) => nextMeetingToast(toast, reached, (url) => { void navigate(url); })); }}
        />
      )}
    </div>
  );
}

/** The Related section for body-less destinations (projects, tags, Views, people). */
export function TaggedItemsSection({ type, id, rollup = false, title = 'Related' }: { type: DestType; id: string; rollup?: boolean; title?: string }) {
  return (
    <section aria-label={title} className="mt-6">
      <h2 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-text-secondary">
        <Scissors size={14} className="text-excerpt" /> {title}
      </h2>
      <ExcerptList mode="destination" type={type} id={id} rollup={rollup} />
    </section>
  );
}
